// frontend/src/components/engineer/pages/ReportsHistoryPage.tsx - FIXED VERSION
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  CalendarDaysIcon,
  PencilSquareIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { AuthUser } from '../../../services/auth.service';
import { 
  reportsService, 
  OvertimeReport, 
  EmployeeReportData, 
  ReportFilters 
} from '../../../services/reports.service';
import { editOvertimeService } from '../../../services/editOvertime.service';
import styles from './ReportsHistoryPage.module.css';

interface ReportsHistoryPageProps {
  user?: AuthUser;
  onNavigate?: (page: string, params?: any) => void;
}

const ReportsHistoryPage: React.FC<ReportsHistoryPageProps> = ({ user, onNavigate }) => {
  // State management
  const [reports, setReports] = useState<OvertimeReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<OvertimeReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [selectedReport, setSelectedReport] = useState<OvertimeReport | null>(null);
  const [employeeData, setEmployeeData] = useState<EmployeeReportData[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isExportingEmployees, setIsExportingEmployees] = useState(false);
  
  // Remark modal state
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  const [isAddingRemark, setIsAddingRemark] = useState(false);
  
  // Filters state
  const [filters, setFilters] = useState<ReportFilters>({
    status: 'all',
    dateFrom: undefined,
    dateTo: undefined,
    projectName: ''
  });

  // Statistics state
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalEmployees: 0,
    totalHours: 0
  });

  // Get user ID for queries
  const userId = user?.employeeNo || user?.employeeNumber || user?.uid || '';
  const userName = user?.name || 'Unknown User';

  // Load reports on component mount and filter changes
  useEffect(() => {
    if (userId) {
      loadReports();
      loadStatistics();
    }
  }, [userId]);

  // Apply filters and search when they change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [reports, filters, searchQuery]);

  // Load reports from service
  const loadReports = useCallback(async () => {
    if (!userId) {
      setError('User not authenticated');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const reportsData = await reportsService.getOvertimeReports(userId, filters);
      setReports(reportsData);
      
      console.log(`✅ Loaded ${reportsData.length} overtime reports`);
    } catch (err) {
      console.error('❌ Error loading reports:', err);
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  }, [userId, filters]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    if (!userId) return;

    try {
      const stats = await reportsService.getReportStatistics(userId);
      setStatistics(stats);
    } catch (err) {
      console.error('❌ Error loading statistics:', err);
    }
  }, [userId]);

  // Apply filters and search
  const applyFiltersAndSearch = useCallback(() => {
    let filtered = [...reports];

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(report =>
        report.projectName.toLowerCase().includes(query) ||
        report.projectCode.toLowerCase().includes(query) ||
        report.requesterName.toLowerCase().includes(query) ||
        report.approverName.toLowerCase().includes(query)
      );
    }

    setFilteredReports(filtered);
  }, [reports, filters, searchQuery]);

  // Handle filter changes
  const handleFilterChange = useCallback((key: keyof ReportFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      status: 'all',
      dateFrom: undefined,
      dateTo: undefined,
      projectName: ''
    });
    setSearchQuery('');
  }, []);

  // Export all reports to Excel
  const handleExportReports = useCallback(async () => {
    if (!userId) return;

    try {
      setIsExporting(true);
      await reportsService.exportOvertimeReports(userId, filters);
    } catch (err) {
      console.error('❌ Error exporting reports:', err);
      setError(err instanceof Error ? err.message : 'Failed to export reports');
    } finally {
      setIsExporting(false);
    }
  }, [userId, filters]);

  // Handle report card click - show employee details
  const handleReportClick = useCallback(async (report: OvertimeReport) => {
    try {
      setSelectedReport(report);
      setIsLoadingEmployees(true);
      
      const employeeData = await reportsService.getEmployeeReport(report.id);
      setEmployeeData(employeeData);
    } catch (err) {
      console.error('❌ Error loading employee data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load employee details');
    } finally {
      setIsLoadingEmployees(false);
    }
  }, []);

  // Export employee report
  const handleExportEmployeeReport = useCallback(async () => {
    if (!selectedReport) return;

    try {
      setIsExportingEmployees(true);
      await reportsService.exportEmployeeReport(selectedReport.id, selectedReport.projectName);
    } catch (err) {
      console.error('❌ Error exporting employee report:', err);
      setError(err instanceof Error ? err.message : 'Failed to export employee report');
    } finally {
      setIsExportingEmployees(false);
    }
  }, [selectedReport]);

  // ✅ FIXED: Check if report is editable
  const canEditReport = useCallback((report: OvertimeReport): boolean => {
  // Only requester can edit
  if (report.requesterId !== userId) {
    return false;
  }
  
  // ✅ NEW: Can edit ONLY if pending (no deadline check)
  if (report.status !== 'pending') {
    return false;
  }
  
  return true; // ✅ No deadline check!
}, [userId]);

  // Handle edit button click
  const handleEditRequest = useCallback((e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    console.log('✏️ Navigating to edit page for request:', requestId);
    onNavigate?.('edit-overtime', requestId);
  }, [onNavigate]);

  // Handle add remark
  const handleAddRemark = useCallback((e: React.MouseEvent, report: OvertimeReport) => {
    e.stopPropagation();
    setSelectedReport(report);
    setRemarkText('');
    setShowRemarkModal(true);
  }, []);

  // Submit remark
  const handleSubmitRemark = useCallback(async () => {
    if (!selectedReport || !remarkText.trim()) return;

    try {
      setIsAddingRemark(true);
      const result = await editOvertimeService.addRemark(
        selectedReport.id,
        remarkText,
        userId,
        userName
      );

      if (result.success) {
        setShowRemarkModal(false);
        setRemarkText('');
        await loadReports();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('❌ Error adding remark:', err);
      setError(err instanceof Error ? err.message : 'Failed to add remark');
    } finally {
      setIsAddingRemark(false);
    }
  }, [selectedReport, remarkText, userId, userName, loadReports]);

  // Close modal
  const closeModal = useCallback(() => {
    setSelectedReport(null);
    setEmployeeData([]);
  }, []);

  // Refresh data
  const handleRefresh = useCallback(() => {
    reportsService.clearCache();
    editOvertimeService.clearCache();
    loadReports();
    loadStatistics();
  }, [loadReports, loadStatistics]);

  // Format date helper
  const formatDate = useCallback((date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  // Format time helper
  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }, []);

  // Render status badge
  const renderStatusBadge = useCallback((status: string) => {
    return (
      <span className={`${styles.statusBadge} ${styles[status]}`}>
        {status}
      </span>
    );
  }, []);

  // Check access permission
  if (!user?.hasOvertimeAccess) {
    return (
      <div className={styles.errorContainer}>
        <ExclamationTriangleIcon className={styles.errorIcon} />
        <h3 className={styles.errorTitle}>Access Denied</h3>
        <p className={styles.errorText}>
          You do not have permission to view overtime reports. Please contact your administrator.
        </p>
        <button 
          onClick={() => onNavigate?.('dashboard')} 
          className={styles.retryButton}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Loading overtime reports...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && reports.length === 0) {
    return (
      <div className={styles.errorContainer}>
        <ExclamationCircleIcon className={styles.errorIcon} />
        <h3 className={styles.errorTitle}>Error Loading Reports</h3>
        <p className={styles.errorText}>{error}</p>
        <button onClick={handleRefresh} className={styles.retryButton}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={styles.reportsContainer}>
      <div className={styles.pageWrapper}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <h1 className={styles.pageTitle}>Reports & History</h1>
            <p className={styles.pageSubtitle}>Track and manage your overtime requests</p>
          </div>
          
          <div className={styles.headerActions}>
            <button
              onClick={handleExportReports}
              disabled={isExporting || filteredReports.length === 0}
              className={styles.exportButton}
            >
              {isExporting ? (
                <>
                  <div className={styles.exportSpinner}></div>
                  Exporting...
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className={styles.buttonIcon} />
                  Export Reports
                </>
              )}
            </button>
            
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className={styles.refreshButton}
            >
              <ArrowPathIcon className={styles.buttonIcon} />
              Refresh
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className={styles.statisticsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <DocumentChartBarIcon className={styles.statIcon} />
            </div>
            <div className={styles.statValue}>{statistics.total}</div>
            <div className={styles.statLabel}>Total Requests</div>
            <div className={styles.statDetail}>
              {statistics.totalEmployees} employees • {statistics.totalHours.toFixed(1)} hours
            </div>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <ClockIcon className={styles.statIcon} />
            </div>
            <div className={styles.statValue}>{statistics.pending}</div>
            <div className={styles.statLabel}>Pending Approval</div>
            <div className={styles.statDetail}>
              Awaiting manager review
            </div>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <CheckCircleIcon className={styles.statIcon} />
            </div>
            <div className={styles.statValue}>{statistics.approved}</div>
            <div className={styles.statLabel}>Approved</div>
            <div className={styles.statDetail}>
              Ready for overtime work
            </div>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <ExclamationCircleIcon className={styles.statIcon} />
            </div>
            <div className={styles.statValue}>{statistics.rejected}</div>
            <div className={styles.statLabel}>Rejected</div>
            <div className={styles.statDetail}>
              Requires resubmission
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className={styles.filtersSection}>
          <div className={styles.filtersHeader}>
            <h3 className={styles.filtersTitle}>Filters</h3>
            <button onClick={clearFilters} className={styles.clearFiltersButton}>
              Clear All
            </button>
          </div>
          
          <div className={styles.filtersGrid}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>From Date</label>
              <input
                type="date"
                value={filters.dateFrom?.toISOString().split('T')[0] || ''}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value ? new Date(e.target.value) : undefined)}
                className={styles.filterInput}
              />
            </div>
            
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>To Date</label>
              <input
                type="date"
                value={filters.dateTo?.toISOString().split('T')[0] || ''}
                onChange={(e) => handleFilterChange('dateTo', e.target.value ? new Date(e.target.value) : undefined)}
                className={styles.filterInput}
              />
            </div>
            
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Project Name</label>
              <input
                type="text"
                placeholder="Search projects..."
                value={filters.projectName || ''}
                onChange={(e) => handleFilterChange('projectName', e.target.value)}
                className={styles.filterInput}
              />
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className={styles.reportsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              Overtime Requests ({filteredReports.length})
            </h2>
            
            <div className={styles.sectionActions}>
              <div className={styles.searchContainer}>
                <MagnifyingGlassIcon className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
          </div>

          <div className={styles.reportsTableContainer}>
            {filteredReports.length > 0 ? (
              <table className={styles.reportsTable}>
                <thead className={styles.tableHeader}>
                  <tr>
                    <th>Project Details</th>
                    <th>Request Date</th>
                    <th>Overtime Schedule</th>
                    <th>Employees</th>
                    <th>Status</th>
                    <th>Approver</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {filteredReports.map((report) => {
                    const isEditable = canEditReport(report);
                    
                    return (
                      <tr
                        key={report.id}
                        className={styles.requestCard}
                        onClick={() => handleReportClick(report)}
                      >
                        <td>
                          <div className={styles.projectInfo}>
                            <div className={styles.projectName}>{report.projectName}</div>
                            <div className={styles.projectCode}>Code: {report.projectCode}</div>
                          </div>
                        </td>
                        
                        <td>
                          <div className={styles.dateText}>
                            {formatDate(report.requestTime)}
                          </div>
                          <div className={styles.timeText}>
                            {formatTime(report.requestTime)}
                          </div>
                        </td>
                        
                        <td>
                          <div className={styles.dateText}>
                            {formatDate(report.startTime)}
                          </div>
                          <div className={styles.timeText}>
                            {formatTime(report.startTime)} - {formatTime(report.endTime)}
                          </div>
                          <div className={styles.hoursText}>
                            {report.totalHours.toFixed(1)} hours
                          </div>
                        </td>
                        
                        <td>
                          <div className={styles.employeeCount}>
                            <UserGroupIcon style={{ width: '1rem', height: '1rem' }} />
                            {report.totalEmployees} employees
                          </div>
                        </td>
                        
                        <td>
                          {renderStatusBadge(report.status)}
                          {report.approvalTime && (
                            <div className={styles.timeText}>
                              {report.status === 'approved' ? 'Approved' : 'Processed'}: {formatDate(report.approvalTime)}
                            </div>
                          )}
                        </td>
                        
                        <td>
                          <div className={styles.approverInfo}>
                            <div>{report.approverName}</div>
                            {report.approvedBy && report.approvedBy !== report.approverName && (
                              <div style={{ fontSize: '0.75rem', color: '#059669' }}>
                                Approved by: {report.approvedBy}
                              </div>
                            )}
                            {report.rejectedBy && (
                              <div style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                                Rejected by: {report.rejectedBy}
                              </div>
                            )}
                          </div>
                        </td>
                        
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {/* View Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReportClick(report);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '0.5rem',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <EyeIcon style={{ width: '1rem', height: '1rem' }} />
                              View
                            </button>

                            {/* Edit Button - Only show if editable */}
                            {isEditable && (
                              <button
                                onClick={(e) => handleEditRequest(e, report.id)}
                                style={{
                                  background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 0.75rem',
                                  borderRadius: '0.5rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <PencilSquareIcon style={{ width: '1rem', height: '1rem' }} />
                                Edit
                              </button>
                            )}

                            {/* Add Remark Button */}
                            <button
                              onClick={(e) => handleAddRemark(e, report)}
                              style={{
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '0.5rem',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <ChatBubbleLeftRightIcon style={{ width: '1rem', height: '1rem' }} />
                              Remark
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className={styles.emptyState}>
                <DocumentChartBarIcon className={styles.emptyStateIcon} />
                <h3 className={styles.emptyStateTitle}>No Reports Found</h3>
                <p className={styles.emptyStateText}>
                  {searchQuery || filters.status !== 'all' || filters.projectName
                    ? 'No reports match your current filters. Try adjusting your search criteria.'
                    : 'You haven\'t created any overtime requests yet.'}
                </p>
                {!searchQuery && filters.status === 'all' && !filters.projectName && (
                  <button
                    onClick={() => onNavigate?.('create-overtime')}
                    className={styles.createFirstButton}
                  >
                    Create Your First Request
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Employee Details Modal */}
        {selectedReport && !showRemarkModal && (
          <div className={styles.modalOverlay} onClick={closeModal}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>
                  Employee Details - {selectedReport.projectName}
                </h3>
                <button onClick={closeModal} className={styles.modalCloseButton}>
                  <XMarkIcon className={styles.modalCloseIcon} />
                </button>
              </div>

              <div className={styles.modalBody}>
                {/* Request Summary */}
                <div style={{
                  background: 'linear-gradient(135deg, #f0fdf4, #f7fee7)',
                  border: '1px solid #bbf7d0',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '1rem'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>
                        Project Code
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                        {selectedReport.projectCode}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>
                        Schedule
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                        {formatTime(selectedReport.startTime)} - {formatTime(selectedReport.endTime)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>
                        Total Hours
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                        {selectedReport.totalHours.toFixed(1)} hours
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>
                        Status
                      </div>
                      <div>
                        {renderStatusBadge(selectedReport.status)}
                      </div>
                    </div>
                  </div>
                  
                  {selectedReport.remarks && (
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>
                        Remarks
                      </div>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: '#047857', 
                        marginTop: '0.25rem',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '100px',
                        overflowY: 'auto',
                        padding: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: '0.375rem'
                      }}>
                        {selectedReport.remarks}
                      </div>
                    </div>
                  )}
                </div>

                {/* Employee Table */}
                {isLoadingEmployees ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Loading employee details...</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className={styles.employeeTable}>
                      <thead>
                        <tr>
                          <th>Employee Name</th>
                          <th>PIN/AUX</th>
                          <th>Designation</th>
                          <th>Department</th>
                          <th>Check-In</th>
                          <th>Check-Out</th>
                          <th>Actual Hours</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeData.map((employee, index) => (
                          <tr key={employee.id || index}>
                            <td style={{ fontWeight: '500' }}>{employee.name}</td>
                            <td>{employee.pin || employee.employeeNumber}</td>
                            <td>{employee.designation}</td>
                            <td>{employee.department}</td>
                            <td>
                              {employee.checkInTime || (
                                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                  Not recorded
                                </span>
                              )}
                            </td>
                            <td>
                              {employee.checkOutTime || (
                                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                  Not recorded
                                </span>
                              )}
                            </td>
                            <td>
                              {employee.actualHours ? (
                                <span style={{ fontWeight: '600', color: '#c29e5d' }}>
                                  {employee.actualHours.toFixed(1)}h
                                </span>
                              ) : (
                                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                  Pending
                                </span>
                              )}
                            </td>
                            <td>
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                background: employee.attendanceStatus === 'present' ? '#d1fae5' : '#fee2e2',
                                color: employee.attendanceStatus === 'present' ? '#065f46' : '#991b1b'
                              }}>
                                {employee.attendanceStatus || 'Pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className={styles.modalActions}>
                <button
                  onClick={handleExportEmployeeReport}
                  disabled={isExportingEmployees}
                  className={`${styles.modalButton} ${styles.primary}`}
                >
                  {isExportingEmployees ? (
                    <>
                      <div className={styles.exportSpinner}></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon style={{ width: '1rem', height: '1rem' }} />
                      Export to Excel
                    </>
                  )}
                </button>
                <button
                  onClick={closeModal}
                  className={`${styles.modalButton} ${styles.secondary}`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remark Modal */}
        {showRemarkModal && selectedReport && (
          <div className={styles.modalOverlay} onClick={() => setShowRemarkModal(false)}>
            <div 
              className={styles.modal} 
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '500px' }}
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>
                  Add Remark - {selectedReport.projectName}
                </h3>
                <button 
                  onClick={() => setShowRemarkModal(false)} 
                  className={styles.modalCloseButton}
                >
                  <XMarkIcon className={styles.modalCloseIcon} />
                </button>
              </div>

              <div className={styles.modalBody}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Your Remark
                  </label>
                  <textarea
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    placeholder="Enter your remark here..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {selectedReport.remarks && (
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginTop: '1rem'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}>
                      Previous Remarks:
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#374151',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '150px',
                      overflowY: 'auto'
                    }}>
                      {selectedReport.remarks}
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.modalActions}>
                <button
                  onClick={() => setShowRemarkModal(false)}
                  className={`${styles.modalButton} ${styles.secondary}`}
                  disabled={isAddingRemark}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitRemark}
                  disabled={!remarkText.trim() || isAddingRemark}
                  className={`${styles.modalButton} ${styles.primary}`}
                  style={{
                    opacity: !remarkText.trim() || isAddingRemark ? 0.5 : 1,
                    cursor: !remarkText.trim() || isAddingRemark ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isAddingRemark ? (
                    <>
                      <div className={styles.exportSpinner}></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <ChatBubbleLeftRightIcon style={{ width: '1rem', height: '1rem' }} />
                      Add Remark
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Toast */}
        {error && (
          <div style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            padding: '1rem',
            maxWidth: '400px',
            zIndex: 1001,
            animation: 'slideInUp 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <ExclamationCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#dc2626', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '0.25rem' }}>
                  Error
                </div>
                <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                  {error}
                </div>
              </div>
              <button
                onClick={() => setError(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#dc2626',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsHistoryPage;